// Port of TrailMap/RegionalTileMapProvider.swift (TrailBivvyKit): a map provider made of official raster tile layers
// for one region (typically a country): a topographic base layer, an optional aerial/satellite layer, optional overlays
// and their attributions.
//
// - Coverage: `coverage` (box) refined by `coveragePolygons`. Every layer without its own coverage inherits the
//   provider's, so offline packages built from `offlineTileLayers(level)` never request tiles outside the region.
// - Style: the satellite base falls back to the fallback base map (Swift: Apple; web: OpenStreetMap) when the region
//   has no aerial layer. A non-opaque base layer (e.g. a low-zoom overview) is drawn over the fallback base map
//   (`usesFallbackBaseMap`).
// - Offline: `routeOnly` → none; other levels → the topographic layer plus the overlays listing the level — only
//   offline-capable layers (terms allow storage and the host sends CORS). Aerial layers are never packaged (size).
// - Protected areas: the injected local (bundled/offline) areas only.

import type {
  DataProvenance,
  EpochMs,
  GeoBounds,
  GeoPolygon,
  OfflinePackageLevel,
  OfflineRegion,
  ProtectedArea,
  TrailRoute,
} from '../core/models';
import { CoverageArea } from '../offline/CoverageArea';
import { withLayerChanges, type TileLayerDescriptor } from '../offline/TileLayerDescriptor';
import { isMapLayerEnabled, type MapLayer, type MapLayerPreferences } from './MapLayer';
import { RouteRegionSplitter } from './RouteRegionSplitter';
import {
  noLocalProtectedAreas,
  type LocalProtectedAreas,
  type MapAttribution,
  type MapStyle,
  type TrailMapProvider,
} from './TrailMapProvider';

/** How a regional provider competes for a location in `CompositeMapProvider`. */
export type RegionalProviderTier =
  /** An official national/regional map: chosen inside its coverage, before any overview. */
  | 'primary'
  /** A coarse overview (e.g. the Europe-wide TopPlusOpen fallback): chosen only where no primary provider applies. */
  | 'overview';

/** A raster overlay of a `RegionalTileMapProvider`, shown when its map-layer toggle is on. */
export interface RegionalOverlay {
  /** The user toggle that shows the overlay (e.g. `slope`, `hikingRoutes`, `elevation`). */
  toggle: MapLayer;
  /** The raster layer. */
  layer: TileLayerDescriptor;
  /** Offline package levels that include the overlay (only when the layer is offline-capable). */
  offlineLevels: ReadonlySet<OfflinePackageLevel>;
}

export interface RegionalTileMapProviderInit {
  /** Stable identifier (e.g. "ign-fr"). */
  id: string;
  /** Provider name (data, not a sentence). */
  displayName: string;
  /** Bounding box of the region; `coveragePolygons` refine it (undefined = the whole box). */
  coverage: GeoBounds;
  coveragePolygons?: GeoPolygon[];
  topographic: TileLayerDescriptor;
  satellite?: TileLayerDescriptor;
  overlays?: RegionalOverlay[];
  /** Licensor page linked from every attribution of this provider. */
  attributionURL?: string;
  /** Shown whenever a layer of this provider is displayed. */
  additionalAttributions?: MapAttribution[];
  /** Licence/provenance records (data for the Sources & Licences screen). */
  provenance?: DataProvenance[];
  /** `primary` for official regional maps, `overview` for coarse fallbacks. */
  tier?: RegionalProviderTier;
  localProtectedAreas?: LocalProtectedAreas;
}

export class RegionalTileMapProvider implements TrailMapProvider {
  readonly id: string;
  readonly displayName: string;
  /** Bounding box of the region. */
  readonly coverage: GeoBounds;
  /** Exact region outline; undefined = the whole `coverage` box. */
  readonly coveragePolygons: GeoPolygon[] | undefined;
  /** Topographic base layer (used for `topographic`). */
  readonly topographic: TileLayerDescriptor;
  /** Aerial/satellite base layer (used for `satellite`); undefined → fallback base map. */
  readonly satellite: TileLayerDescriptor | undefined;
  /** Raster overlays, bottom to top. */
  readonly overlays: RegionalOverlay[];
  readonly attributionURL: string | undefined;
  readonly additionalAttributions: MapAttribution[];
  readonly provenance: DataProvenance[];
  /** The provider's area (`coverage` ∩ `coveragePolygons`). */
  readonly coverageArea: CoverageArea;
  readonly tier: RegionalProviderTier;
  private readonly localProtectedAreas: LocalProtectedAreas;
  private readonly init: RegionalTileMapProviderInit;

  constructor(init: RegionalTileMapProviderInit) {
    this.init = init;
    const scoped = (layer: TileLayerDescriptor): TileLayerDescriptor => {
      if (layer.coverage !== undefined || layer.coveragePolygons !== undefined) return layer;
      return withLayerChanges(layer, { coverage: init.coverage, coveragePolygons: init.coveragePolygons });
    };
    this.id = init.id;
    this.displayName = init.displayName;
    this.coverage = init.coverage;
    this.coveragePolygons = init.coveragePolygons;
    this.topographic = scoped(init.topographic);
    this.satellite = init.satellite ? scoped(init.satellite) : undefined;
    this.overlays = (init.overlays ?? []).map((o) => ({ toggle: o.toggle, layer: scoped(o.layer), offlineLevels: o.offlineLevels }));
    this.attributionURL = init.attributionURL;
    this.additionalAttributions = init.additionalAttributions ?? [];
    this.provenance = init.provenance ?? [];
    this.coverageArea = new CoverageArea(init.coverage, init.coveragePolygons);
    this.tier = init.tier ?? 'primary';
    this.localProtectedAreas = init.localProtectedAreas ?? noLocalProtectedAreas;
  }

  /** The same provider with another source of local protected areas. */
  withLocalProtectedAreas(source: LocalProtectedAreas): RegionalTileMapProvider {
    return new RegionalTileMapProvider({ ...this.init, localProtectedAreas: source });
  }

  /** Every raster layer of the provider (topographic, satellite, overlays). */
  get allLayers(): TileLayerDescriptor[] {
    return [this.topographic, ...(this.satellite ? [this.satellite] : []), ...this.overlays.map((o) => o.layer)];
  }

  /** All attributions this provider can require (for the Sources & Licences screen). */
  get attributions(): MapAttribution[] {
    return this.attributionsFor(this.allLayers);
  }

  /**
   * Attributions for the given displayed layers: each layer's text (linked to `attributionURL`), then
   * `additionalAttributions`; de-duplicated, in order. Empty when no layer is displayed.
   */
  attributionsFor(layers: readonly TileLayerDescriptor[]): MapAttribution[] {
    if (layers.length === 0) return [];
    const result: MapAttribution[] = [];
    const candidates: MapAttribution[] = [
      ...layers.map((l) => (this.attributionURL ? { text: l.attribution, url: this.attributionURL } : { text: l.attribution })),
      ...this.additionalAttributions,
    ];
    for (const attribution of candidates) {
      if (!result.some((a) => a.text === attribution.text)) result.push(attribution);
    }
    return result;
  }

  // MARK: TrailMapProvider

  /**
   * Base: `topographic`, or `satellite` when chosen (fallback base map when the region has none). Overlays whose toggle
   * is enabled, in declaration order. `usesFallbackBaseMap` when there is no raster base or it is not opaque.
   */
  mapStyle(_bounds: GeoBounds, preferences: MapLayerPreferences): MapStyle {
    const base = preferences.baseLayer === 'satellite' ? this.satellite : this.topographic;
    const rasterOverlays = this.overlays.filter((o) => isMapLayerEnabled(preferences, o.toggle)).map((o) => o.layer);
    const shown = [...(base ? [base] : []), ...rasterOverlays];
    const style: MapStyle = {
      rasterOverlays,
      usesFallbackBaseMap: !(base?.isOpaque ?? false),
      attributions: this.attributionsFor(shown),
    };
    if (base) style.baseLayer = base;
    return style;
  }

  /**
   * `routeOnly`: none. Other levels: the topographic layer plus overlays listing the level, filtered to offline-capable
   * layers. Each layer carries the provider's coverage.
   */
  offlineTileLayers(level: OfflinePackageLevel): TileLayerDescriptor[] {
    if (level === 'routeOnly') return [];
    const layers = [this.topographic, ...this.overlays.filter((o) => o.offlineLevels.has(level)).map((o) => o.layer)];
    return layers.filter((l) => l.offlineCapable);
  }

  /**
   * Regions of ≤ 40 km (stage boundaries when possible), ids "region:<routeID>:<index>:<level>", with size estimates
   * counting only the tiles inside this provider's coverage.
   */
  downloadableRegions(route: TrailRoute, level: OfflinePackageLevel, now: EpochMs): OfflineRegion[] {
    return RouteRegionSplitter.regions(route, level, this.offlineTileLayers(level), now);
  }

  /** Local areas only (no official online source is queried for these regions yet). */
  protectedAreas(bounds: GeoBounds): Promise<ProtectedArea[]> {
    return this.localProtectedAreas(bounds);
  }
}
